import { useCallback, useEffect, useRef, useState } from "react";
import { adaptVideo, getFeed, getVideo, getContentMenu, IS_DEMO } from "./api";
import { videos, type VideoItem } from "./data";
const message = (error: unknown) =>
  error instanceof Error ? error.message : "读取失败，请重试";
export function useCatalog() {
  const [items, setItems] = useState<VideoItem[]>(IS_DEMO ? videos : []);
  const [loading, setLoading] = useState(!IS_DEMO);
  const [moreLoading, setMoreLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [total, setTotal] = useState(IS_DEMO ? videos.length : 0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ types: string[]; labels: string[] }>({
    types: [],
    labels: [],
  });
  const [menuError, setMenuError] = useState("");
  const request = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const details = useRef(new Map<string, VideoItem>());
  const activeDetail = useRef("");
  const detailRequest = useRef<AbortController | null>(null);
  const load = useCallback(async (next = "") => {
    if (IS_DEMO || busy.current) return;
    busy.current = true;
    setError("");
    next ? setMoreLoading(true) : setLoading(true);
    const controller = new AbortController();
    request.current = controller;
    try {
      const response = await getFeed(next, controller.signal);
      if (controller.signal.aborted) return;
      const mapped = response.items.map(
        (v) => details.current.get(v.id) || adaptVideo(v),
      );
      setItems((existing) =>
        next
          ? [
              ...existing,
              ...mapped.filter((v) => !existing.some((e) => e.id === v.id)),
            ]
          : mapped,
      );
      setTotal(response.total);
      setCursor(response.hasMore ? response.nextCursor : null);
    } catch (error) {
      if (!controller.signal.aborted) setError(message(error));
    } finally {
      if (request.current === controller) {
        busy.current = false;
        setLoading(false);
        setMoreLoading(false);
      }
    }
  }, []);
  const loadDetail = useCallback(async (id: string) => {
    if (IS_DEMO || !id || id.startsWith("local-")) return;
    activeDetail.current = id;
    detailRequest.current?.abort();
    setDetailError("");
    const cached = details.current.get(id);
    if (cached) {
      setDetailLoading(false);
      setItems((s) =>
        s.map((v) =>
          v.id === id
            ? { ...cached, src: v.src || cached.src, episode: v.episode }
            : v,
        ),
      );
      return;
    }
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetailLoading(true);
    try {
      const item = adaptVideo(await getVideo(id, controller.signal));
      if (controller.signal.aborted) return;
      details.current.set(id, item);
      setItems((s) =>
        s.some((v) => v.id === id)
          ? s.map((v) => (v.id === id ? item : v))
          : [item, ...s],
      );
    } catch (error) {
      if (!controller.signal.aborted && activeDetail.current === id)
        setDetailError(message(error));
    } finally {
      if (!controller.signal.aborted && activeDetail.current === id)
        setDetailLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
    return () => {
      request.current?.abort();
      detailRequest.current?.abort();
      busy.current = false;
    };
  }, [load]);
  useEffect(() => {
    if (IS_DEMO) return;
    const controller = new AbortController();
    getContentMenu(controller.signal)
      .then(setMenu)
      .catch(() => {
        if (!controller.signal.aborted)
          setMenuError("分类配置暂时无法读取，先按视频类型查看");
      });
    return () => controller.abort();
  }, []);
  return {
    items,
    setItems,
    loading,
    moreLoading,
    error,
    detailError,
    detailLoading,
    total,
    menu,
    menuError,
    hasMore: !!cursor,
    loadMore: () => void load(cursor || ""),
    retry: () => void load(),
    loadDetail,
  };
}
